import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { experimentsService } from '@wix/cashier-common/dist/src/utils/experimentsService';
import { PaymentService } from '@wix/cashier-core/dist/src/services/Payment';
import { RedirectTarget } from '@wix/cashier-core/dist/src/services/Payment/types';
import { CreditCardFormAPI } from '@wix/payments-cc-form/dist/lib/CreditCardFormAPI';
import { BillingInfo } from '@wix/payments-cc-form/dist/src/types/BillingInfo';
import { TokenizeResponse } from '@wix/payments-cc-form/dist/src/types/TokenizeResponse';
import { Experiment } from '../../../types/Experiment';
import {
  ICashierPublicCallbacks,
  ICashierSDKConfiguration,
  IQueryConfig,
} from '../../cashier-sdk';
import { PaymentMethodsEvents, PaymentMethodsStatuses } from '../../types';
import { MFieldsNested } from '../../types/MField';
import { ModulePayOptions } from '../../types/ModulePayOptions';
import { buildPaymentDetails } from '../../utils/buildPaymentDetails';
import { createExpectedError } from '../../utils/expectedError';
import { paymentPopup } from '../../utils/PaymentPopup';
import { squareTestCardNonceWith3DS } from '../../utils/squareTestCardNonceWith3DS';
import {
  PMethodModule,
  PMethodModuleConfiguration,
} from '../cashier-sdk-modules';
import PaymentModule from './PaymentModule';

export interface CreditCardConfiguration
  extends ICashierPublicCallbacks,
    PMethodModuleConfiguration {
  orderId?: string;
  visitorId?: string;
  query: IQueryConfig;
  paymentModule?: PaymentModule;
  host?: string;
  sdkInitConfig: ICashierSDKConfiguration;
  getCardFormApi(): CreditCardFormAPI;
}

const extractCardBillingInfo = (mFieldsNested: MFieldsNested): BillingInfo => {
  const { taxIdentifier, issuerBank, ...billingInfo } = mFieldsNested;

  return billingInfo;
};

class CreditCard implements PMethodModule {
  private props: CreditCardConfiguration;
  public name: string;

  constructor({
    visitorId,
    host,
    paymentModule,
    orderId,
    query,
    getCardFormApi,
    sdkInitConfig,
  }: CreditCardConfiguration) {
    this.props = {
      visitorId,
      host,
      paymentModule,
      orderId,
      query,
      sdkInitConfig,
      getCardFormApi: getCardFormApi || (() => null),
    };
  }

  public validate = () => this.props.getCardFormApi().validate();

  public getCreditCardPublicData = () =>
    this.props.getCardFormApi().getPublicData();

  public collapse = () => this.getCreditCardPublicData();

  public expand = () =>
    Promise.resolve({
      method: PaymentMethod.CreditCard,
      type: PaymentMethodsEvents.EXPAND,
      status: PaymentMethodsStatuses.EXPANDED,
    });

  public tokenize = async (params: {
    instance: string;
    providerId: PaymentProvider;
    mFieldsNested?: MFieldsNested;
  }): Promise<TokenizeResponse> => {
    const { instance, mFieldsNested, providerId } = params;
    let tokenizeResponse: TokenizeResponse;

    try {
      tokenizeResponse = await this.props.getCardFormApi().tokenize({
        appInstance: instance,
        billingInfo: mFieldsNested ? extractCardBillingInfo(mFieldsNested) : {},
        // @ts-ignore
        full: true, // remove after cc-form@1.432.0 GAx2
      });
    } catch (error) {
      throw createExpectedError({
        errorMessage: `Failed to tokenize credit card`,
        errorData: error,
      });
    }

    if (
      providerId === PaymentProvider.Square &&
      this.props.sdkInitConfig.squareVerifyBuyer &&
      experimentsService.isExperimentEnabled(Experiment.EnableSquareVerifyBuyer)
    ) {
      const availability =
        this.props.sdkInitConfig.isSquareVerifyBuyerAvailable();

      if (availability.isAvailable) {
        if (
          experimentsService.isExperimentEnabled(
            Experiment.UseSquareTestCardNonceWith3DS
          )
        ) {
          // if this FT is on we're doing buyer verification with the fake card nonce below, and the actual nonce that
          // we pass to the backend should equal (otherwise Square will reject with "payment sources mismatch")
          tokenizeResponse.numberToken = squareTestCardNonceWith3DS;
        }

        tokenizeResponse.securityCodeToken =
          await this.props.paymentModule.squareVerifyBuyer(
            tokenizeResponse.numberToken
          );
      } else {
        console.error(
          `Square Verify Buyer will be skipped due to missing required data: ${availability.missingData}`
        );
      }
    }

    return tokenizeResponse;
  };

  public pay = async (
    mFieldsNested: MFieldsNested = {},
    options: ModulePayOptions
  ) => {
    const {
      saveCreditCard = false,
      instance = null,
      creditCardId = '',
      frameable = true,
      responsive = true,
      installments,
      providerId,
      revalidateAppInstance,
      orderId = this.props.orderId,
    } = options;
    const redirectTarget = this.props.paymentModule.getRedirectTarget({
      paymentMethod: PaymentMethod.CreditCard,
      frameable,
    });
    const inNewTab = redirectTarget === RedirectTarget.NewWindow;

    if (inNewTab && !paymentPopup.isOpened()) {
      this.props.paymentModule.preOpenPaymentPopup(PaymentMethod.CreditCard);
    }

    const tokenizeResponse = await this.tokenize({
      mFieldsNested,
      instance,
      providerId,
    });

    options.onBeforeCharge();

    const chargeResponse = await new PaymentService({
      baseURL: this.props.host,
      appInstance: instance,
      revalidateAppInstance,
    }).chargeOrder(
      orderId,
      {
        payment: {
          details: await buildPaymentDetails({
            paymentMethod: PaymentMethod.CreditCard,
            mFieldsNested,
            redirectTarget,
            installments,
            locale: this.props.query.locale,
            visitorId: this.props.visitorId,
            tokenizeResponse,
            saveCreditCard,
          }),
        },
      },
      { fullInfo: false }
    );

    return this.props.paymentModule.continuePayment(PaymentMethod.CreditCard, {
      chargeResponse,
      inNewTab,
      saveCreditCard,
      instance,
      creditCardId,
      frameable,
      responsive,
      mFieldsNested,
    });
  };
}

export default CreditCard;
